import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api, mediaUrl } from '@/lib/api';
import { trackEvent } from '@/components/PGTracker';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Shuffle,
  Repeat,
  Repeat1,
  ListMusic,
  X,
  ChevronDown,
  ChevronUp,
  Music as MusicIcon,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const AudioPlayerContext = createContext(null);

export function useGlobalAudio() {
  const ctx = useContext(AudioPlayerContext);
  if (!ctx) {
    return {
      queue: [],
      currentIndex: -1,
      current: null,
      isPlaying: false,
      shuffle: false,
      repeat: 'off',
      playlistMode: false,
      continueOpen: false,
      continueSuggestions: [],
      resumeCandidate: null,
      setQueueAndPlay: () => {},
      playTrack: () => {},
      playAll: () => {},
      togglePlay: () => {},
      stop: () => {},
      next: () => {},
      prev: () => {},
      clearQueue: () => {},
      setShuffle: () => {},
      setRepeat: () => {},
      setPlaylistMode: () => {},
      setContinueOpen: () => {},
      doResume: () => {},
      dismissResume: () => {},
    };
  }
  return ctx;
}

const STORAGE_KEY = 'pg_global_player_v1';

function pickAudioSrc(item) {
  if (!item) return '';
  if (item.source_type === 'embed' && item.embed_url) return item.embed_url;
  if (item.file_url) return mediaUrl(item.file_url);
  return '';
}

function formatTime(s) {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
}

export function GlobalAudioPlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);
  const [muted, setMuted] = useState(false);
  // Playback semantics (Phase 17 expansion):
  //   playlistMode = false  → Play Now single-track behavior. Track finishes,
  //                            player halts and shows Continue Listening panel.
  //   playlistMode = true   → Play All / Playlist behavior. Auto-advance
  //                            through the queue honoring shuffle + repeat.
  const [playlistMode, setPlaylistMode] = useState(false);
  // Shuffle randomizes queue traversal while ensuring every track plays once
  // before repeating. Regenerates on toggle + at end-of-cycle under Repeat All.
  const [shuffle, setShuffleState] = useState(false);
  const [shuffleOrder, setShuffleOrder] = useState([]); // list of queue indices
  const [shufflePos, setShufflePos] = useState(0);      // position within shuffleOrder
  // Repeat is a 3-state cycle: 'off' → 'one' → 'all' → 'off'.
  const [repeat, setRepeat] = useState('off');
  const [expanded, setExpanded] = useState(false);
  const [showQueue, setShowQueue] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // Resume playback — { id, title, position, cover_url, kind } from last session
  const [resumeCandidate, setResumeCandidate] = useState(null);
  const [resumeDismissed, setResumeDismissed] = useState(false);
  // Continue Listening — shown when a Play-Now track finishes and playlistMode = false
  const [continueOpen, setContinueOpen] = useState(false);
  const [continueSuggestions, setContinueSuggestions] = useState([]);

  // Hydrate volume + repeat + shuffle prefs and Resume candidate
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (typeof saved.volume === 'number') setVolume(saved.volume);
        if (typeof saved.muted === 'boolean') setMuted(saved.muted);
        if (typeof saved.shuffle === 'boolean') setShuffleState(saved.shuffle);
        if (saved.repeat === 'one' || saved.repeat === 'all') setRepeat(saved.repeat);
      }
      const resumeRaw = localStorage.getItem(`${STORAGE_KEY}:resume`);
      if (resumeRaw) {
        const parsed = JSON.parse(resumeRaw);
        if (parsed && parsed.id && typeof parsed.position === 'number' && parsed.position > 5) {
          setResumeCandidate(parsed);
        }
      }
    } catch (e) { /* noop */ }
  }, []);

  // Persist prefs
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ volume, muted, shuffle, repeat })
      );
    } catch (e) { /* noop */ }
  }, [volume, muted, shuffle, repeat]);

  const current = currentIndex >= 0 ? queue[currentIndex] || null : null;

  // Persist a Resume snapshot every ~5s while a track is playing.
  useEffect(() => {
    if (!current || !isPlaying) return undefined;
    const t = setInterval(() => {
      try {
        const snap = {
          id: current.id,
          title: current.title,
          cover_url: current.cover_url || null,
          kind: current.kind || null,
          position: currentTime,
          savedAt: Date.now(),
        };
        localStorage.setItem(`${STORAGE_KEY}:resume`, JSON.stringify(snap));
      } catch (_) { /* noop */ }
    }, 5000);
    return () => clearInterval(t);
  }, [current, isPlaying, currentTime]);

  const isEmbed = current?.source_type === 'embed' && !!current?.embed_url;
  const src = pickAudioSrc(current);

  // Apply volume / mute on audio element
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = volume;
    a.muted = muted;
  }, [volume, muted]);

  // Load + play when current track changes
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !src || isEmbed) return;
    a.src = src;
    a.load();
    if (isPlaying) {
      a.play().catch(() => setIsPlaying(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // Toggle native play/pause when isPlaying changes externally
  useEffect(() => {
    const a = audioRef.current;
    if (!a || !src || isEmbed) return;
    if (isPlaying) {
      const p = a.play();
      if (p && typeof p.catch === 'function') p.catch(() => setIsPlaying(false));
    } else {
      a.pause();
    }
  }, [isPlaying, src, isEmbed]);

  const playIndex = useCallback((idx) => {
    setCurrentIndex(idx);
    setIsPlaying(true);
    setDismissed(false);
    setContinueOpen(false);
  }, []);

  // Fisher-Yates shuffle of [0..n-1], keeping `startIndex` at position 0 when provided.
  const _generateShuffleOrder = useCallback((length, startIndex = 0) => {
    if (length <= 1) return [0];
    const indices = Array.from({ length }, (_, i) => i);
    // Take startIndex out of the pool then shuffle the rest.
    const rest = indices.filter((i) => i !== startIndex);
    for (let i = rest.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [rest[i], rest[j]] = [rest[j], rest[i]];
    }
    return [startIndex, ...rest];
  }, []);

  const setQueueAndPlay = useCallback((items, startIndex = 0, opts = {}) => {
    const list = (items || []).filter((it) => pickAudioSrc(it));
    if (list.length === 0) return;
    const safeIndex = Math.max(0, Math.min(startIndex, list.length - 1));
    setQueue(list);
    setDismissed(false);
    // Playlist mode is entered when the list contains multiple tracks OR when
    // caller explicitly opts in via { playlistMode: true }.
    const enterPlaylist = (opts && typeof opts.playlistMode === 'boolean')
      ? opts.playlistMode
      : list.length > 1;
    setPlaylistMode(enterPlaylist);
    // Regenerate shuffle order if shuffle is on and we're entering a real playlist.
    if (shuffle && enterPlaylist) {
      const order = _generateShuffleOrder(list.length, safeIndex);
      setShuffleOrder(order);
      setShufflePos(0);
    } else {
      setShuffleOrder([]);
      setShufflePos(0);
    }
    // schedule index in next tick so src effect picks up new queue
    setTimeout(() => playIndex(safeIndex), 0);
  }, [playIndex, shuffle, _generateShuffleOrder]);

  const playTrack = useCallback((item, contextList = null) => {
    if (!item) return;
    if (contextList && contextList.length > 0) {
      const list = contextList.filter((it) => pickAudioSrc(it));
      const idx = list.findIndex((t) => t.id === item.id);
      if (idx >= 0) {
        setQueueAndPlay(list, idx);
        return;
      }
    }
    // Single-item Play Now: playlistMode=false regardless of prior state.
    setQueueAndPlay([item], 0, { playlistMode: false });
  }, [setQueueAndPlay]);

  // Play All — dedicated helper: takes the full library and starts Playlist Mode.
  // If a currently-playing track appears in the list, we preserve its position
  // (no restart). Otherwise start from index 0. Repeat One takes precedence
  // over transitioning into playlist mode per spec.
  const playAll = useCallback((items) => {
    const list = (items || []).filter((it) => pickAudioSrc(it));
    if (list.length === 0) return;
    // Repeat One exception — do not disturb the currently repeating song.
    if (repeat === 'one' && current) {
      // Enter playlist mode conceptually but leave the current track playing.
      setQueue(list);
      const idx = list.findIndex((t) => t.id === current.id);
      const startIdx = idx >= 0 ? idx : 0;
      setCurrentIndex(startIdx);
      setPlaylistMode(true);
      if (shuffle) {
        setShuffleOrder(_generateShuffleOrder(list.length, startIdx));
        setShufflePos(0);
      }
      return;
    }
    // Seamless transition when the same track is already playing.
    if (current && list.some((t) => t.id === current.id)) {
      const idx = list.findIndex((t) => t.id === current.id);
      setQueue(list);
      setCurrentIndex(idx);
      setPlaylistMode(true);
      if (shuffle) {
        setShuffleOrder(_generateShuffleOrder(list.length, idx));
        setShufflePos(0);
      }
      // Keep audio element playing without reload; the src effect key won't change
      // because currentIndex is set on the new queue with the same track object.
      return;
    }
    setQueueAndPlay(list, 0, { playlistMode: true });
  }, [current, repeat, shuffle, setQueueAndPlay, _generateShuffleOrder]);

  // Toggle Shuffle. When enabling mid-playlist we regenerate order so the
  // current track stays "now playing" and the remainder is randomized.
  const setShuffle = useCallback((v) => {
    setShuffleState(v);
    if (v && queue.length > 1) {
      setShuffleOrder(_generateShuffleOrder(queue.length, currentIndex >= 0 ? currentIndex : 0));
      setShufflePos(0);
    } else {
      setShuffleOrder([]);
      setShufflePos(0);
    }
  }, [queue.length, currentIndex, _generateShuffleOrder]);

  const dismissResume = useCallback(() => {
    setResumeDismissed(true);
    setResumeCandidate(null);
    try { localStorage.removeItem(`${STORAGE_KEY}:resume`); } catch (_) { /* noop */ }
  }, []);

  const doResume = useCallback(async () => {
    // Try to find the track from the library so playback resumes with rich metadata.
    if (!resumeCandidate) return;
    try {
      const { data } = await api.get('/audio');
      const track = (data || []).find((t) => t.id === resumeCandidate.id);
      if (track) {
        setQueueAndPlay([track], 0, { playlistMode: false });
        // Seek to saved position after load.
        setTimeout(() => {
          const a = audioRef.current;
          if (a && Number.isFinite(resumeCandidate.position)) {
            try { a.currentTime = resumeCandidate.position; } catch (_) { /* noop */ }
          }
        }, 400);
      }
    } catch (_) { /* noop */ }
    setResumeCandidate(null);
  }, [resumeCandidate, setQueueAndPlay]);

  // Continue Listening — populated when a Play-Now track ends outside Playlist Mode.
  const openContinueListening = useCallback(async (justFinishedId) => {
    try {
      const { data } = await api.get('/audio');
      const pool = (data || [])
        .filter((t) => (t.kind === 'music' || t.kind === 'reflection') && t.id !== justFinishedId && pickAudioSrc(t));
      // Shuffle a small sample of up to 8 tracks.
      const shuffled = pool.slice().sort(() => Math.random() - 0.5).slice(0, 8);
      setContinueSuggestions(shuffled);
    } catch (_) {
      setContinueSuggestions([]);
    }
    setContinueOpen(true);
  }, []);

  const togglePlay = useCallback(() => {
    if (!current) return;
    setIsPlaying((p) => !p);
  }, [current]);

  // Stop = pause + reset the audio position to 0. Used by Featured Headliner
  // and any UI that wants a hard stop (vs pause).
  const stop = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      try {
        a.pause();
        a.currentTime = 0;
      } catch (_) { /* noop */ }
    }
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  // Advance playback based on Playlist Mode / Shuffle / Repeat semantics.
  // - Repeat One  → replays the current track (only when called by button; onEnded handles the audio restart directly).
  // - Playlist Mode + Shuffle: walk the shuffleOrder; regenerate at end if Repeat All.
  // - Playlist Mode without Shuffle: linear next; wrap when Repeat All.
  // - Not Playlist Mode: end of a Play-Now single track → stop and (for onEnded caller) surface Continue Listening.
  const advanceNext = useCallback((triggeredByEnd = false) => {
    if (queue.length === 0) return;
    if (repeat === 'one') {
      // The audio element handles the actual re-loop via onEnded.
      // For an explicit next() press we still stay on the current track.
      return;
    }
    if (!playlistMode) {
      // Play Now single-track. Stop + surface Continue Listening if the track just ended.
      setIsPlaying(false);
      if (triggeredByEnd && current) openContinueListening(current.id);
      return;
    }
    // Playlist Mode
    if (shuffle && shuffleOrder.length > 0) {
      const nextPos = shufflePos + 1;
      if (nextPos < shuffleOrder.length) {
        setShufflePos(nextPos);
        setCurrentIndex(shuffleOrder[nextPos]);
        setIsPlaying(true);
        return;
      }
      // End of shuffled order.
      if (repeat === 'all') {
        // Regenerate a fresh shuffled order (starting from a random index) and continue.
        const order = _generateShuffleOrder(queue.length, Math.floor(Math.random() * queue.length));
        setShuffleOrder(order);
        setShufflePos(0);
        setCurrentIndex(order[0]);
        setIsPlaying(true);
        return;
      }
      setIsPlaying(false);
      return;
    }
    // Linear playlist mode
    setCurrentIndex((idx) => {
      if (idx + 1 < queue.length) return idx + 1;
      if (repeat === 'all') return 0;
      setIsPlaying(false);
      return idx;
    });
  }, [queue.length, queue, repeat, playlistMode, shuffle, shuffleOrder, shufflePos, current, _generateShuffleOrder, openContinueListening]);

  const next = useCallback(() => advanceNext(false), [advanceNext]);

  const prev = useCallback(() => {
    const a = audioRef.current;
    if (a && a.currentTime > 3) {
      a.currentTime = 0;
      return;
    }
    if (playlistMode && shuffle && shuffleOrder.length > 0) {
      const prevPos = shufflePos - 1;
      if (prevPos >= 0) {
        setShufflePos(prevPos);
        setCurrentIndex(shuffleOrder[prevPos]);
        setIsPlaying(true);
        return;
      }
      // Wrap under Repeat All only.
      if (repeat === 'all') {
        const lastPos = shuffleOrder.length - 1;
        setShufflePos(lastPos);
        setCurrentIndex(shuffleOrder[lastPos]);
        setIsPlaying(true);
        return;
      }
      return;
    }
    setCurrentIndex((idx) => {
      if (queue.length === 0) return idx;
      if (idx - 1 >= 0) return idx - 1;
      if (playlistMode && repeat === 'all') return queue.length - 1;
      return idx;
    });
  }, [queue.length, playlistMode, shuffle, shuffleOrder, shufflePos, repeat]);

  const seekTo = useCallback((pct) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    a.currentTime = (pct / 100) * a.duration;
  }, []);

  const clearQueue = useCallback(() => {
    setIsPlaying(false);
    setQueue([]);
    setCurrentIndex(-1);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  const removeFromQueue = useCallback((id) => {
    setQueue((q) => {
      const idx = q.findIndex((t) => t.id === id);
      if (idx < 0) return q;
      const newQ = q.slice(0, idx).concat(q.slice(idx + 1));
      setCurrentIndex((ci) => {
        if (ci === idx) {
          // play next if available; else stop
          if (newQ.length === 0) {
            setIsPlaying(false);
            return -1;
          }
          return Math.min(ci, newQ.length - 1);
        }
        if (idx < ci) return ci - 1;
        return ci;
      });
      return newQ;
    });
  }, []);

  const value = useMemo(() => ({
    queue,
    currentIndex,
    current,
    isPlaying,
    currentTime,
    duration,
    volume,
    muted,
    shuffle,
    repeat,
    playlistMode,
    expanded,
    showQueue,
    dismissed,
    resumeCandidate,
    continueOpen,
    continueSuggestions,
    setQueueAndPlay,
    playTrack,
    playAll,
    playIndex,
    togglePlay,
    stop,
    next,
    prev,
    seekTo,
    setVolume,
    setMuted,
    setShuffle,
    setRepeat,
    setPlaylistMode,
    setExpanded,
    setShowQueue,
    setDismissed,
    setContinueOpen,
    clearQueue,
    removeFromQueue,
    doResume,
    dismissResume,
  }), [
    queue, currentIndex, current, isPlaying, currentTime, duration,
    volume, muted, shuffle, repeat, playlistMode, expanded, showQueue, dismissed,
    resumeCandidate, continueOpen, continueSuggestions,
    setQueueAndPlay, playTrack, playAll, playIndex, togglePlay, stop, next, prev,
    seekTo, setShuffle, clearQueue, removeFromQueue, doResume, dismissResume,
  ]);

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
      {/* The persistent <audio> element lives here, outside any route */}
      <audio
        ref={audioRef}
        preload="metadata"
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        onEnded={() => {
          const c = current;
          if (c?.id) {
            trackEvent({
              event_type: 'media_complete',
              content_type: c.type || 'audio_item',
              content_id: c.id,
              meta: { title: c.title || '' },
            });
          }
          if (repeat === 'one') {
            const a = audioRef.current;
            if (a) {
              a.currentTime = 0;
              a.play().catch(() => {});
            }
          } else {
            advanceNext(true);
          }
        }}
        onPlay={() => {
          setIsPlaying(true);
          const c = current;
          if (c?.id) {
            // media_start once per track — guard using dataset attribute on audio element
            const a = audioRef.current;
            if (a && a.dataset.pgStartedFor !== c.id) {
              a.dataset.pgStartedFor = c.id;
              trackEvent({
                event_type: 'media_start',
                content_type: c.type || 'audio_item',
                content_id: c.id,
                meta: { title: c.title || '' },
              });
            }
          }
        }}
        onPause={(e) => {
          // Guard against races with the linear-advance path: when the media
          // element ends, the browser fires 'pause' just before the 'ended'
          // handler runs. Ignoring that pause prevents overwriting the
          // isPlaying=true state that the advance path sets.
          if (!e.currentTarget.ended) setIsPlaying(false);
        }}
        style={{ display: 'none' }}
      />
      <PersistentPlayerBar />
      <ResumeListeningPrompt />
      <ContinueListeningModal />
      <QueuePanel />
    </AudioPlayerContext.Provider>
  );
}

/* -------------------------------- Queue --------------------------------- */

function QueuePanel() {
  const {
    queue, current, currentIndex, showQueue, setShowQueue,
    playIndex, removeFromQueue, clearQueue,
    shuffle, shuffleOrder, playlistMode,
  } = useGlobalAudio();

  if (!showQueue || !current) return null;

  // Determine traversal order: shuffled indices in Playlist Mode + Shuffle,
  // otherwise queue order. Now Playing is always first; Up Next is index+1;
  // Remaining Playlist trails behind.
  const traversal = (shuffle && playlistMode && shuffleOrder.length === queue.length)
    ? shuffleOrder
    : queue.map((_, i) => i);
  const currentPosInTraversal = traversal.indexOf(currentIndex);
  const upcomingIndices = currentPosInTraversal >= 0
    ? traversal.slice(currentPosInTraversal + 1)
    : traversal;

  return (
    <div
      className="fixed z-[75] right-3 sm:right-6 bottom-24 sm:bottom-24 w-[92vw] sm:w-[420px] max-h-[70vh] pg-card overflow-hidden flex flex-col"
      style={{ boxShadow: '0 12px 40px rgba(0,0,0,0.55)' }}
      data-testid="player-queue-panel"
    >
      <div className="p-4 border-b border-[var(--pg-border-2)] flex items-center justify-between">
        <div>
          <p className="pg-label text-[10px]">QUEUE</p>
          <p className="text-[10px] text-[var(--pg-ink-faint)] font-mono">
            {queue.length} track{queue.length === 1 ? '' : 's'}
            {shuffle && playlistMode ? ' · SHUFFLED' : ''}
            {playlistMode ? ' · PLAYLIST' : ' · SINGLE'}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={clearQueue}
            data-testid="player-queue-clear"
            className="pg-btn-ghost rounded-[var(--pg-radius-sm)] px-2 py-1 text-[10px] font-mono tracking-widest text-[var(--pg-neon-pink)]"
          >CLEAR</button>
          <button
            onClick={() => setShowQueue(false)}
            data-testid="player-queue-close"
            aria-label="Close queue"
            className="p-2 text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]"
          >
            <X size={14} />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto pg-scroll">
        <section className="p-3">
          <p className="text-[10px] font-mono tracking-widest text-[var(--pg-emerald-2)] uppercase mb-2">Now Playing</p>
          <QueueRow item={current} index={currentIndex} onPlay={() => {}} onRemove={() => removeFromQueue(current.id)} active />
        </section>
        {upcomingIndices.length > 0 && (
          <section className="p-3 border-t border-[var(--pg-border-2)]">
            <p className="text-[10px] font-mono tracking-widest text-[var(--pg-gold-2)] uppercase mb-2">Up Next · {upcomingIndices.length}</p>
            <ul className="space-y-1">
              {upcomingIndices.map((qi) => (
                <li key={queue[qi]?.id || qi}>
                  <QueueRow item={queue[qi]} index={qi} onPlay={() => playIndex(qi)} onRemove={() => removeFromQueue(queue[qi]?.id)} />
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}

function QueueRow({ item, onPlay, onRemove, active }) {
  if (!item) return null;
  return (
    <div className={`flex items-center gap-3 p-2 rounded-md ${active ? 'bg-[rgba(25,194,142,0.08)] border border-[var(--pg-emerald)]' : 'hover:bg-[rgba(242,238,227,0.05)]'}`} data-testid="player-queue-row">
      <button
        onClick={onPlay}
        disabled={active}
        aria-label={active ? 'Now playing' : `Play ${item.title}`}
        className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${active ? 'bg-[var(--pg-emerald)] text-[var(--pg-ink)]' : 'border border-[var(--pg-border-2)] text-[var(--pg-ink-muted)] hover:text-[var(--pg-emerald)]'}`}
      >
        <Play size={12} className="ml-0.5" />
      </button>
      <div className="h-8 w-8 rounded-md overflow-hidden bg-[var(--pg-navy-800)] shrink-0 border border-[var(--pg-border-2)]">
        {item.cover_url ? <img src={mediaUrl(item.cover_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[var(--pg-emerald)]"><MusicIcon size={11} /></div>}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-serif text-[var(--pg-ink)] truncate">{item.title}</p>
        <p className="text-[10px] font-mono text-[var(--pg-ink-faint)] uppercase truncate">{item.kind || 'audio'}{item.duration ? ` · ${item.duration}` : ''}</p>
      </div>
      <button
        onClick={onRemove}
        aria-label={`Remove ${item.title}`}
        className="p-1.5 text-[var(--pg-ink-faint)] hover:text-[var(--pg-neon-pink)]"
      >
        <X size={12} />
      </button>
    </div>
  );
}

/* ------------------------- Resume Listening Toast ------------------------ */

function ResumeListeningPrompt() {
  const { resumeCandidate, doResume, dismissResume } = useGlobalAudio();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (resumeCandidate) {
      const t = setTimeout(() => setVisible(true), 900); // small delay so it doesn't jump on load
      return () => clearTimeout(t);
    }
    setVisible(false);
    return undefined;
  }, [resumeCandidate]);
  if (!resumeCandidate || !visible) return null;
  return (
    <div
      className="fixed z-[70] left-1/2 -translate-x-1/2 bottom-24 sm:bottom-8 max-w-md w-[92%] pg-card p-3 flex items-center gap-3 border border-[var(--pg-emerald)]"
      data-testid="player-resume-prompt"
      style={{ boxShadow: '0 12px 40px rgba(0,0,0,0.55), 0 0 24px rgba(25,194,142,0.20)' }}
    >
      <div className="h-10 w-10 rounded-md overflow-hidden bg-[var(--pg-navy-800)] border border-[var(--pg-border-2)] shrink-0">
        {resumeCandidate.cover_url ? <img src={mediaUrl(resumeCandidate.cover_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[var(--pg-emerald)]"><MusicIcon size={16} /></div>}
      </div>
      <div className="flex-1 min-w-0">
        <p className="pg-label text-[10px]" style={{ color: 'var(--pg-emerald-2)' }}>RESUME LISTENING</p>
        <p className="text-sm text-[var(--pg-ink)] truncate font-serif">{resumeCandidate.title}</p>
        <p className="text-[10px] text-[var(--pg-ink-faint)] font-mono">from {formatTime(resumeCandidate.position || 0)}</p>
      </div>
      <button
        onClick={doResume}
        data-testid="player-resume-play"
        className="pg-btn-primary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-[10px] font-mono tracking-widest"
      >RESUME</button>
      <button
        onClick={dismissResume}
        data-testid="player-resume-dismiss"
        className="text-[var(--pg-ink-faint)] hover:text-[var(--pg-ink)]"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}

/* -------------------------- Continue Listening --------------------------- */

function ContinueListeningModal() {
  const { continueOpen, continueSuggestions, setContinueOpen, playTrack, playAll } = useGlobalAudio();
  if (!continueOpen) return null;
  return (
    <AnimatePresence>
      {continueOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-3 sm:p-6"
          onClick={() => setContinueOpen(false)}
          data-testid="player-continue-modal"
        >
          <div className="absolute inset-0 bg-[rgba(4,6,12,0.75)] backdrop-blur-md" />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-3xl pg-card overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-6 border-b border-[var(--pg-border-2)] flex items-center justify-between">
              <div>
                <p className="pg-label text-[10px]">CONTINUE LISTENING</p>
                <h3 className="font-serif text-2xl mt-1 text-[var(--pg-ink)]">Keep the room warm.</h3>
                <p className="text-xs text-[var(--pg-ink-muted)] mt-1">The song ended — here&apos;s more from the Music library.</p>
              </div>
              <button onClick={() => setContinueOpen(false)} data-testid="player-continue-close" className="p-2 text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]" aria-label="Close">
                <X size={16} />
              </button>
            </div>
            <div className="p-4 sm:p-5 max-h-[60vh] overflow-y-auto pg-scroll">
              {continueSuggestions.length === 0 ? (
                <p className="text-sm text-[var(--pg-ink-muted)] text-center py-8">No additional tracks available right now.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {continueSuggestions.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => { playTrack(t); setContinueOpen(false); }}
                      data-testid="player-continue-item"
                      className="text-left group pg-card overflow-hidden hover:border-[var(--pg-emerald)] transition-colors"
                    >
                      <div className="aspect-square bg-[var(--pg-navy-800)] relative">
                        {t.cover_url ? <img src={mediaUrl(t.cover_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[var(--pg-emerald)]"><MusicIcon size={22} /></div>}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-[rgba(4,6,12,0.35)]">
                          <span className="h-11 w-11 rounded-full bg-[var(--pg-emerald)] flex items-center justify-center"><Play size={14} className="ml-0.5 text-[var(--pg-ink)]" /></span>
                        </div>
                      </div>
                      <div className="p-2">
                        <p className="text-xs font-serif text-[var(--pg-ink)] truncate">{t.title}</p>
                        <p className="text-[10px] font-mono text-[var(--pg-ink-faint)] uppercase truncate">{t.kind || 'music'}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {continueSuggestions.length > 0 && (
              <div className="p-4 border-t border-[var(--pg-border-2)] flex items-center justify-between">
                <p className="text-[10px] font-mono text-[var(--pg-ink-faint)]">Tip: tap Play All on the Music page for continuous playback.</p>
                <button
                  onClick={() => { playAll(continueSuggestions); setContinueOpen(false); }}
                  data-testid="player-continue-play-all"
                  className="pg-btn-primary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-[10px] font-mono tracking-widest"
                >PLAY ALL SUGGESTIONS</button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function PersistentPlayerBar() {
  const {
    queue, currentIndex, current, isPlaying, currentTime, duration,
    volume, muted, shuffle, repeat, playlistMode, expanded, showQueue, dismissed,
    resumeCandidate, continueOpen, continueSuggestions,
    togglePlay, next, prev, seekTo, setVolume, setMuted, setShuffle,
    setRepeat, setExpanded, setShowQueue, setDismissed, playIndex,
    removeFromQueue, clearQueue, setContinueOpen, doResume, dismissResume, playTrack,
  } = useGlobalAudio();

  const [compact, setCompact] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile breakpoint (<768px)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const apply = () => setIsMobile(mq.matches);
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else mq.removeListener(apply);
    };
  }, []);

  // Mobile-first: auto-minimize on scroll down past threshold
  useEffect(() => {
    if (!isMobile) { setCompact(false); return undefined; }
    let lastY = window.scrollY;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const y = window.scrollY;
        const goingDown = y > lastY;
        const goingUp = y < lastY - 4;
        if (goingDown && y > 80 && !compact) setCompact(true);
        else if (goingUp && compact) setCompact(false);
        lastY = y;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isMobile, compact]);

  if (!current || dismissed) return null;

  const isEmbed = current.source_type === 'embed' && !!current.embed_url;
  const progress = duration ? (currentTime / duration) * 100 : 0;

  const RepeatIcon = repeat === 'one' ? Repeat1 : Repeat;
  const repeatActive = repeat !== 'off';
  // Cycle Repeat: off → one → all → off
  const cycleRepeat = () => setRepeat(repeat === 'off' ? 'one' : repeat === 'one' ? 'all' : 'off');
  const toggleRepeat = cycleRepeat;
  const shuffleActive = !!shuffle;
  const toggleShuffle = () => setShuffle(!shuffle);
  const repeatTitle = repeat === 'one'
    ? 'Repeat one (on) — tap for Repeat All'
    : repeat === 'all'
      ? 'Repeat all (on) — tap to turn off'
      : 'Repeat off — tap for Repeat One';
  const repeatAriaLabel = repeat === 'one'
    ? 'Repeat one'
    : repeat === 'all'
      ? 'Repeat all'
      : 'Repeat off';

  const onSeek = (e) => {
    const value = Number(e.target.value);
    seekTo(value);
  };

  // ===== Compact mobile bar =====
  if (isMobile && compact && !expanded) {
    return (
      <div
        data-testid="persistent-player"
        className="fixed left-0 right-0 bottom-0 z-50"
        style={{ pointerEvents: 'none' }}
      >
        <motion.button
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          onClick={() => setCompact(false)}
          aria-label="Expand player"
          data-testid="persistent-player-compact"
          className="mx-auto mb-3 max-w-[92%] flex items-center gap-3 px-3 py-2 rounded-full border border-[var(--pg-border-2)] backdrop-blur-xl bg-[rgba(10,7,18,0.92)] shadow-[0_18px_40px_-12px_rgba(0,0,0,0.6)]"
          style={{ pointerEvents: 'auto' }}
        >
          <span className="relative h-9 w-9 rounded-full overflow-hidden bg-[var(--pg-navy-800)] shrink-0 border border-[var(--pg-border-2)]">
            {current.cover_url ? (
              <img src={mediaUrl(current.cover_url)} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-[var(--pg-emerald)]"><MusicIcon size={14} /></div>
            )}
            {isPlaying && (
              <span className="absolute inset-0 rounded-full" style={{ boxShadow: '0 0 0 2px rgba(25,194,142,0.65), 0 0 16px rgba(25,194,142,0.55)' }} />
            )}
          </span>
          <span className="min-w-0 flex-1 text-left">
            <span className="block font-serif text-[13px] text-[var(--pg-ink)] truncate" data-testid="player-track-title">{current.title}</span>
            <span className="block text-[10px] font-mono text-[var(--pg-ink-faint)] tracking-widest uppercase truncate">{current.kind || 'audio'}</span>
          </span>
          <span
            onClick={(e) => { e.stopPropagation(); toggleRepeat(); }}
            role="button"
            tabIndex={0}
            aria-label={repeat === 'one' ? 'Repeat one — turn off' : 'Repeat one — turn on'}
            aria-pressed={repeatActive}
            data-testid="player-repeat-mobile"
            className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 border ${repeatActive ? 'text-[var(--pg-emerald)] border-[var(--pg-emerald)] bg-[rgba(25,194,142,0.12)]' : 'text-[var(--pg-ink-muted)] border-[var(--pg-border-2)]'}`}
          >
            <RepeatIcon size={13} />
          </span>
          <span
            onClick={(e) => { e.stopPropagation(); togglePlay(); }}
            role="button"
            tabIndex={0}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            data-testid="player-play"
            className="h-9 w-9 rounded-full bg-[var(--pg-emerald)] hover:bg-[var(--pg-emerald-2)] text-[var(--pg-ink)] flex items-center justify-center shadow-[0_6px_18px_rgba(14,107,85,0.30)] cursor-pointer shrink-0"
          >
            {isPlaying ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
          </span>
        </motion.button>
      </div>
    );
  }

  return (
    <div
      data-testid="persistent-player"
      className="fixed left-0 right-0 bottom-0 z-50"
      style={{ pointerEvents: 'none' }}
    >
      <AnimatePresence>
        {expanded && (
          <motion.div
            key="expanded"
            initial={{ opacity: 1, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="pg-glass mx-auto mb-2 max-w-3xl rounded-[var(--pg-radius-md)] border border-[var(--pg-border-2)] backdrop-blur-xl bg-[rgba(15,10,22,0.78)]"
            style={{ pointerEvents: 'auto' }}
          >
            <div className="p-5">
              <div className="flex items-start gap-4">
                <div className="h-20 w-20 rounded-md overflow-hidden bg-[var(--pg-navy-800)] shrink-0 border border-[var(--pg-border-2)]">
                  {current.cover_url ? (
                    <img src={mediaUrl(current.cover_url)} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-[var(--pg-emerald)]">
                      <MusicIcon size={22} />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-[10px] tracking-[0.32em] pg-text-glow-emerald uppercase">{current.kind || 'audio'}</p>
                  <h4 className="font-serif text-lg text-[var(--pg-ink)] truncate mt-1">{current.title}</h4>
                  {current.description && (
                    <p className="text-xs text-[var(--pg-ink-muted)] mt-1 line-clamp-2">{current.description}</p>
                  )}
                </div>
                <button
                  onClick={toggleRepeat}
                  className={`p-2 rounded-md border ${repeatActive ? 'text-[var(--pg-emerald)] border-[var(--pg-emerald)] bg-[rgba(25,194,142,0.12)]' : 'text-[var(--pg-ink-muted)] border-[var(--pg-border-2)] hover:text-[var(--pg-ink)]'}`}
                  data-testid="player-repeat-expanded"
                  aria-label={repeat === 'one' ? 'Repeat one — turn off' : 'Repeat one — turn on'}
                  aria-pressed={repeatActive}
                  title={repeat === 'one' ? 'Repeat one (on) — tap to turn off' : 'Repeat one (off) — tap to loop current track'}
                >
                  <RepeatIcon size={16} />
                </button>
                <button
                  onClick={() => setExpanded(false)}
                  className="p-2 rounded-md text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)] border border-[var(--pg-border-2)]"
                  data-testid="player-collapse-button"
                  aria-label="Collapse player"
                >
                  <ChevronDown size={16} />
                </button>
              </div>

              {isEmbed && (
                <div className="mt-4 rounded-[var(--pg-radius-md)] overflow-hidden border border-[var(--pg-border-2)]">
                  <iframe
                    title={current.title}
                    src={current.embed_url}
                    width="100%"
                    height="152"
                    frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    style={{ display: 'block', background: 'var(--pg-navy-900)' }}
                  />
                </div>
              )}
            </div>

            {showQueue && (
              <div className="border-t border-[var(--pg-border-2)] px-5 py-3 max-h-64 overflow-y-auto">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] tracking-[0.32em] pg-text-glow-gold uppercase">UP NEXT · {queue.length} TRACKS</span>
                  <button onClick={clearQueue} className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] hover:text-[var(--pg-ink)]">CLEAR</button>
                </div>
                <ul className="divide-y divide-[var(--pg-border-2)]">
                  {queue.map((it, i) => {
                    const active = i === currentIndex;
                    return (
                      <li key={`${it.id}-${i}`} className="py-2 flex items-center gap-3">
                        <span className="font-mono text-[10px] text-[var(--pg-ink-faint)] w-6 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                        <button
                          onClick={() => playIndex(i)}
                          className="flex-1 text-left min-w-0"
                          data-testid="queue-item"
                        >
                          <p className={`text-sm truncate ${active ? 'pg-text-glow-emerald' : 'text-[var(--pg-ink)]'}`}>{it.title}</p>
                          <p className="text-[10px] font-mono text-[var(--pg-ink-faint)] tracking-widest uppercase">{it.kind || 'audio'}{it.duration ? ` · ${it.duration}` : ''}</p>
                        </button>
                        <button
                          onClick={() => removeFromQueue(it.id)}
                          className="p-1 text-[var(--pg-ink-faint)] hover:text-[var(--pg-neon-pink)]"
                          aria-label="Remove from queue"
                        >
                          <X size={12} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className="border-t border-[var(--pg-border-2)] backdrop-blur-xl bg-[rgba(10,7,18,0.86)]"
        style={{ pointerEvents: 'auto' }}
      >
        {/* progress bar */}
        <div className="px-4 sm:px-6 lg:px-10 pt-2">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] text-[var(--pg-ink-faint)] tracking-widest w-10 shrink-0">{formatTime(currentTime)}</span>
            <input
              type="range"
              min={0}
              max={100}
              value={isFinite(progress) ? progress : 0}
              onChange={onSeek}
              disabled={isEmbed}
              className="flex-1 accent-[var(--pg-emerald)] h-1 bg-[var(--pg-navy-800)] rounded-full appearance-none cursor-pointer"
              data-testid="player-seek"
              aria-label="Seek"
            />
            <span className="font-mono text-[10px] text-[var(--pg-ink-faint)] tracking-widest w-10 shrink-0 text-right">{formatTime(duration)}</span>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center gap-3 sm:gap-5">
          {/* track meta */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="h-11 w-11 rounded-md overflow-hidden bg-[var(--pg-navy-800)] shrink-0 border border-[var(--pg-border-2)]">
              {current.cover_url ? (
                <img src={mediaUrl(current.cover_url)} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-[var(--pg-emerald)]">
                  <MusicIcon size={16} />
                </div>
              )}
            </div>
            <div className="min-w-0">
              <p className="font-serif text-sm text-[var(--pg-ink)] truncate" data-testid="player-track-title">{current.title}</p>
              <p className="font-mono text-[10px] text-[var(--pg-ink-faint)] tracking-widest uppercase truncate">{current.kind || 'audio'}{current.duration ? ` · ${current.duration}` : ''}</p>
            </div>
          </div>

          {/* primary controls */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <button
              onClick={toggleShuffle}
              className={`hidden sm:inline-flex p-2 rounded-md ${shuffleActive ? 'text-[var(--pg-emerald)]' : 'text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]'}`}
              aria-label={shuffleActive ? 'Shuffle on — tap to disable' : 'Shuffle off — tap to enable'}
              aria-pressed={shuffleActive}
              title={shuffleActive ? 'Shuffle on' : 'Shuffle off'}
              data-testid="player-shuffle"
            >
              <Shuffle size={15} />
            </button>
            <button
              onClick={prev}
              className="p-2 rounded-md text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]"
              aria-label="Previous"
              data-testid="player-prev"
              disabled={queue.length === 0}
            >
              <SkipBack size={17} />
            </button>
            <button
              onClick={togglePlay}
              className="h-10 w-10 rounded-full bg-[var(--pg-emerald)] hover:bg-[var(--pg-emerald-2)] text-[var(--pg-ink)] flex items-center justify-center shadow-[0_8px_24px_rgba(14,107,85,0.35)] transition-colors"
              aria-label={isPlaying ? 'Pause' : 'Play'}
              data-testid="player-play"
            >
              {isPlaying ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
            </button>
            <button
              onClick={next}
              className="p-2 rounded-md text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]"
              aria-label="Next"
              data-testid="player-next"
              disabled={queue.length === 0}
            >
              <SkipForward size={17} />
            </button>
            <button
              onClick={cycleRepeat}
              className={`inline-flex p-2 rounded-md ${repeatActive ? 'text-[var(--pg-emerald)]' : 'text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]'}`}
              aria-label={repeatAriaLabel}
              aria-pressed={repeatActive}
              title={repeatTitle}
              data-testid="player-repeat"
            >
              <RepeatIcon size={15} />
            </button>
          </div>

          {/* right side: volume + queue + collapse + close */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <button
              onClick={() => setMuted(!muted)}
              className="p-2 rounded-md text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]"
              aria-label={muted ? 'Unmute' : 'Mute'}
              data-testid="player-mute"
            >
              {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
            </button>
            <input
              type="range"
              min={0}
              max={100}
              value={Math.round((muted ? 0 : volume) * 100)}
              onChange={(e) => {
                const v = Number(e.target.value) / 100;
                setVolume(v);
                if (v > 0 && muted) setMuted(false);
              }}
              className="hidden md:block w-20 accent-[var(--pg-emerald)] h-1 bg-[var(--pg-navy-800)] rounded-full appearance-none cursor-pointer"
              aria-label="Volume"
              data-testid="player-volume"
            />
            <button
              onClick={() => { setExpanded(true); setShowQueue((q) => !q); }}
              className={`p-2 rounded-md ${showQueue ? 'text-[var(--pg-gold-2)]' : 'text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]'}`}
              aria-label="Toggle queue"
              data-testid="player-queue-toggle"
            >
              <ListMusic size={15} />
            </button>
            <button
              onClick={() => setExpanded((e) => !e)}
              className="p-2 rounded-md text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]"
              aria-label={expanded ? 'Collapse' : 'Expand'}
              data-testid="player-expand"
            >
              {expanded ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
            </button>
            <button
              onClick={() => setDismissed(true)}
              className="p-2 rounded-md text-[var(--pg-ink-faint)] hover:text-[var(--pg-neon-pink)]"
              aria-label="Close player"
              data-testid="player-close"
              title="Close player (will reappear next time you play a track)"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
